/**
 * api/forms.js
 * All REST API calls for the forms CRUD page.
 * Base endpoint: /wp-json/wpbean-pgs/v1/settings
 */

import apiFetch from "@wordpress/api-fetch";

const BASE = "/wpbean-pgs/v1/settings";

/**
 * @typedef {Object} Setting
 * @property {string} id
 * @property {string} title
 * @property {string} status      - "publish" | "draft" | "trash"
 * @property {string} created_at
 */

/**
 * @typedef {Object} FormsResponse
 * @property {Form[]} items
 * @property {number} total
 */

export const getForms = ({
    page = 1,
    per_page = 10,
    search = "",
    status = "",
} = {}) => {
    const query = new URLSearchParams({
        page,
        per_page,
        ...(search && { search }),
        ...(status && { status }),
    }).toString();
    return apiFetch({ path: `${BASE}?${query}` });
};

export const getForm = (id) => apiFetch({ path: `${BASE}/${id}` });

export const createForm = (data) =>
    apiFetch({ path: BASE, method: "POST", data });

/**
 * Full update — replaces title, status, and config.
 * Used by the form builder Save button.
 */
export const updateForm = (id, data) =>
    apiFetch({ path: `${BASE}/${id}`, method: "PUT", data });

/**
 * Partial update — only sends the fields you provide.
 * Uses PATCH so PHP only writes the keys that are present in the request.
 */
export const patchForm = (id, data) =>
    apiFetch({ path: `${BASE}/${id}`, method: "PATCH", data });

export const duplicateForm = async (id) => {
    const form = await getForm(id);
    return createForm({
        title: `${form.title} (Copy)`,
        status: "draft",
        config: form.config ?? {},
    });
};

/**
 * Toggles status between "publish" and "draft".
 * Uses PATCH so only the status column is touched in the DB.
 */
export const toggleFormStatus = (id, currentStatus) =>
    patchForm(id, {
        status: currentStatus === "publish" ? "draft" : "publish",
    });

/** Move to trash (soft delete — recoverable). */
export const trashForm = (id) =>
    apiFetch({ path: `${BASE}/${id}/trash`, method: "POST" });

/** Restore a trashed form back to draft. */
export const restoreForm = (id) =>
    apiFetch({ path: `${BASE}/${id}/restore`, method: "POST" });

/** Hard delete — only call this after user confirmation. */
export const permanentDeleteForm = (id) =>
    apiFetch({ path: `${BASE}/${id}`, method: "DELETE" });

// ── Bulk ──────────────────────────────────────────────────────────────────────

export const bulkTrashForms = (ids) => Promise.all(ids.map(trashForm));
export const bulkRestoreForms = (ids) => Promise.all(ids.map(restoreForm));
export const bulkPermanentDeleteForms = (ids) =>
    Promise.all(ids.map(permanentDeleteForm));
export const bulkUpdateStatus = (ids, status) =>
    Promise.all(ids.map((id) => patchForm(id, { status })));
